import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  IoAddOutline,
  IoChevronDownOutline,
  IoCloseOutline,
  IoMicOutline,
  IoSearchOutline,
} from "react-icons/io5";
import Menu from "components/common/Menu";
import { CommonStyles } from "styles/CommonStyles";
import { useTabActive } from "hook/useTabActive";
import { SideBarTabs } from "constants/tabs";
import {
  createDealer,
  createMasterProfile,
  createDealerMember,
  deleteDealerMember,
  getAdminDealerSubmissions,
  getAdminDealerInventory,
  getAdminImporterPrices,
  getAdminMasterProfiles,
  getCarMakes,
  getDealerLeads,
  getDealerMembers,
  getDealers,
  updateAdminDealerInventory,
  updateAdminImporterPrice,
  updateAdminDealerSubmission,
  updateDealer,
  updateDealerMember,
  updateDealerLead,
  updateMasterProfile,
  publishDealerSubmission,
} from "api/services/dealer";
import {
  AdminImporterPriceType,
  CarMakeType,
  DealerInventoryLeadType,
  DealerInventoryType,
  DealerLeadStatusType,
  DealerMemberRoleType,
  DealerMemberType,
  DealerSubmissionStatusType,
  DealerSubmissionType,
  DealerType,
  MasterCarProfileType,
} from "types/loadedData";
import { getSpeechRecognition } from "utils/speechRecognition";
import { dashboardConfirm } from "i18n/dashboardLanguage";

type TabType =
  | "prices"
  | "dealers"
  | "inventory"
  | "submissions"
  | "leads"
  | "models";
type PublishFormType = {
  make: string;
  model: string;
  year: string;
  price: string;
  fuelType: string;
  color: string;
  batteryRangeKm: string;
  isDeal: boolean;
  description: string;
};
type SubmissionStatusFilterType =
  | "All"
  | "Review"
  | "Posted"
  | "Unavailable"
  | "Rejected";

const submissionImageSlots = ["front", "back", "interior"];
const submissionSlotOrder = ["front", "back", "interior", "deal", "detail", "extra"];
const getSortedSubmissionImages = (submission: DealerSubmissionType) =>
  [...(submission.images || [])].sort((left, right) => {
    const leftSlot = submissionSlotOrder.indexOf(left.slot);
    const rightSlot = submissionSlotOrder.indexOf(right.slot);
    if (leftSlot !== rightSlot) return leftSlot - rightSlot;
    return Number(left.order || 0) - Number(right.order || 0);
  });
const adminSubmissionFilters: SubmissionStatusFilterType[] = [
  "All",
  "Review",
  "Posted",
  "Unavailable",
  "Rejected",
];
const getSubmissionStatusView = (
  status: DealerSubmissionStatusType
): Exclude<SubmissionStatusFilterType, "All"> => {
  if (status === "Published") return "Posted";
  if (status === "Unavailable" || status === "Sold") return "Unavailable";
  if (status === "Rejected") return "Rejected";
  return "Review";
};
const getBackendStatusFromView = (
  status: Exclude<SubmissionStatusFilterType, "All">
): DealerSubmissionStatusType => {
  if (status === "Rejected") return "Rejected";
  if (status === "Unavailable") return "Unavailable";
  if (status === "Posted") return "Published";
  return "Submitted";
};
const isBatteryFuel = (fuelType = "") =>
  ["electric", "hybrid"].includes(String(fuelType).toLowerCase());
const isElectricFuel = (fuelType = "") =>
  String(fuelType).toLowerCase() === "electric";
const fuelOptions = ["Electric", "Hybrid", "Gas", "Diesel"];
const getTelHref = (phone = "") => `tel:${phone.replace(/[^\d+]/g, "")}`;

function PhoneLink({ phone }: { phone?: string }) {
  if (!phone) return <span>No phone</span>;
  return (
    <a className={ClassName.phoneLink} href={getTelHref(phone)}>
      {phone}
    </a>
  );
}

function DealerMarketplacePage() {
  useTabActive(SideBarTabs.DealerMarketplace.name);
  const location = useLocation();
  const selectedLeadId = String(location.state?.leadId || "");
  const [activeTab, setActiveTab] = useState<TabType>("dealers");
  const [dealers, setDealers] = useState<DealerType[]>([]);
  const [dealerMembers, setDealerMembers] = useState<DealerMemberType[]>([]);
  const [inventory, setInventory] = useState<DealerInventoryType[]>([]);
  const [submissions, setSubmissions] = useState<DealerSubmissionType[]>([]);
  const [leads, setLeads] = useState<DealerInventoryLeadType[]>([]);
  const [carMakes, setCarMakes] = useState<CarMakeType[]>([]);
  const [masterProfiles, setMasterProfiles] = useState<MasterCarProfileType[]>([]);
  const [importerPrices, setImporterPrices] = useState<AdminImporterPriceType[]>([]);
  const [priceInputs, setPriceInputs] = useState<Record<string, string>>({});
  const [savingPriceId, setSavingPriceId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [pageErrors, setPageErrors] = useState<string[]>([]);
  const [searchValue, setSearchValue] = useState("");
  const [inventorySuggestions, setInventorySuggestions] = useState<string[]>([]);
  const [listening, setListening] = useState(false);

  useEffect(() => {
    if (selectedLeadId) setActiveTab("leads");
  }, [selectedLeadId]);

  useEffect(() => {
    if (!selectedLeadId || activeTab !== "leads" || !leads.length) return;
    document.getElementById(`lead-${selectedLeadId}`)?.scrollIntoView({
      behavior: "smooth",
      block: "center",
    });
  }, [activeTab, leads, selectedLeadId]);
  const [modelForm, setModelForm] = useState({
    make: "",
    model: "",
    year: "",
    fuelType: "Electric",
    color: "",
    batteryRangeKm: "",
  });
  const [publishForms, setPublishForms] = useState<
    Record<string, PublishFormType>
  >({});
  const [openSubmissionId, setOpenSubmissionId] = useState<string | null>(null);
  const [submissionStatusFilter, setSubmissionStatusFilter] =
    useState<SubmissionStatusFilterType>("All");
  const [editingDealerId, setEditingDealerId] = useState<string | null>(null);
  const [dealerFormOpen, setDealerFormOpen] = useState(false);
  const [dealerAdvancedOpen, setDealerAdvancedOpen] = useState(false);
  const [memberPanelOpen, setMemberPanelOpen] = useState(false);
  const dealerFormRef = useRef<HTMLFormElement>(null);
  const [dealerSaving, setDealerSaving] = useState(false);
  const [dealerFormError, setDealerFormError] = useState("");
  const [dealerForm, setDealerForm] = useState({
    name: "",
    phoneNumber: "",
    email: "",
    password: "",
    notes: "",
    trustedDealer: false,
    isActive: true,
    portalRole: "Importer" as "Importer" | "Admin",
  });
  const [dealerMemberForm, setDealerMemberForm] = useState({
    dealer: "",
    userPhone: "",
    role: "Staff" as DealerMemberRoleType,
    canDirectPost: true,
    isActive: true,
  });

  useEffect(() => {
    loadAll();
  }, []);

  const loadAll = async (search = searchValue) => {
    setLoading(true);
    const nextErrors: string[] = [];
    const loadResource = async <T,>(
      label: string,
      request: () => Promise<T>,
      apply: (value: T) => void
    ) => {
      try {
        apply(await request());
      } catch (error) {
        nextErrors.push(`${label} could not load.`);
      }
    };

    try {
      const trimmedSearch = search.trim();
      await Promise.all([
        loadResource("Dealers", () => getDealers(trimmedSearch), setDealers),
        loadResource(
          "Dealer app links",
          () => getDealerMembers(trimmedSearch),
          setDealerMembers
        ),
        loadResource(
          "Inventory",
          () => getAdminDealerInventory(undefined, trimmedSearch),
          (result) => {
            setInventory(result.inventory);
            setInventorySuggestions(result.suggestions || []);
          }
        ),
        loadResource(
          "Dealer submissions",
          () => getAdminDealerSubmissions(trimmedSearch),
          (result) => setSubmissions(result.submissions)
        ),
        loadResource("Leads", () => getDealerLeads(trimmedSearch), setLeads),
        loadResource("Car brands", getCarMakes, setCarMakes),
        loadResource(
          "Master profiles",
          () => getAdminMasterProfiles(trimmedSearch),
          setMasterProfiles
        ),
        loadResource(
          "Importer prices",
          () => getAdminImporterPrices(trimmedSearch),
          setImporterPrices
        ),
      ]);
      setPageErrors(nextErrors);
    } finally {
      setLoading(false);
    }
  };

  const resetDealerForm = () => {
    setEditingDealerId(null);
    setDealerFormOpen(false);
    setDealerAdvancedOpen(false);
    setDealerFormError("");
    setDealerForm({
      name: "",
      phoneNumber: "",
      email: "",
      password: "",
      notes: "",
      trustedDealer: false,
      isActive: true,
      portalRole: "Importer" as "Importer" | "Admin",
    });
  };

  const handleAddDealer = () => {
    resetDealerForm();
    setActiveTab("dealers");
    setDealerFormOpen(true);
    window.requestAnimationFrame(() => {
      dealerFormRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    });
  };

  const handleSaveDealer = async (event: React.FormEvent) => {
    event.preventDefault();
    if (dealerSaving) return;

    const name = dealerForm.name.trim();
    const phoneNumber = dealerForm.phoneNumber.trim();
    const password = dealerForm.password.trim();
    if (!name || !phoneNumber || (!editingDealerId && !password)) {
      setDealerFormError(
        "Importer name, phone number, and password are required."
      );
      return;
    }

    setDealerSaving(true);
    setDealerFormError("");
    setMessage("");
    const payload = {
      name,
      phoneNumber,
      email: dealerForm.email.trim(),
      notes: dealerForm.notes.trim(),
      trustedDealer: dealerForm.trustedDealer,
      isActive: dealerForm.isActive,
      portalRole: dealerForm.portalRole,
      ...(password ? { password } : {}),
    };
    try {
      const dealer = editingDealerId
        ? await updateDealer(editingDealerId, payload)
        : await createDealer({ ...payload, password });
      const wasEditing = Boolean(editingDealerId);
      setDealers((current) =>
        editingDealerId
          ? current.map((item) => (item._id === dealer._id ? dealer : item))
          : [dealer, ...current]
      );
      resetDealerForm();
      setMessage(
        wasEditing ? "Importer account updated." : "Importer account created."
      );
    } catch (error: any) {
      setDealerFormError(
        error?.response?.data?.msg ||
          error?.response?.data?.error ||
          "The importer account could not be saved. Please try again."
      );
    } finally {
      setDealerSaving(false);
    }
  };

  const handleSearch = (event?: React.FormEvent) => {
    event?.preventDefault();
    loadAll(searchValue.trim());
  };

  const handleSuggestion = (suggestion: string) => {
    setSearchValue(suggestion);
    loadAll(suggestion);
  };

  const startVoiceSearch = () => {
    const SpeechRecognition = getSpeechRecognition();
    if (!SpeechRecognition || listening) return;

    const recognition = new SpeechRecognition();
    recognition.lang = "en-US";
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;
    recognition.onstart = () => setListening(true);
    recognition.onend = () => setListening(false);
    recognition.onerror = () => setListening(false);
    recognition.onresult = (event: any) => {
      const transcript = event.results?.[0]?.[0]?.transcript?.trim();
      if (!transcript) return;
      setSearchValue(transcript);
      loadAll(transcript);
    };
    recognition.start();
  };

  const handleEditDealer = (dealer: DealerType) => {
    setEditingDealerId(dealer._id);
    setDealerFormOpen(true);
    setDealerAdvancedOpen(true);
    setDealerFormError("");
    setMessage("");
    setDealerForm({
      name: dealer.name || "",
      phoneNumber: dealer.phoneNumber || "",
      email: dealer.email || "",
      password: "",
      notes: dealer.notes || "",
      trustedDealer: Boolean(dealer.trustedDealer),
      isActive: Boolean(dealer.isActive),
      portalRole: dealer.portalRole === "Admin" ? "Admin" : "Importer",
    });
    window.requestAnimationFrame(() => {
      dealerFormRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    });
  };

  const handleCreateMasterProfile = async (event: React.FormEvent) => {
    event.preventDefault();
    if (
      !modelForm.make ||
      !modelForm.model.trim() ||
      !modelForm.year ||
      !modelForm.fuelType
    ) {
      return;
    }
    const profile = await createMasterProfile({
      make: modelForm.make,
      model: modelForm.model.trim(),
      year: modelForm.year,
      fuelType: modelForm.fuelType,
      color: modelForm.color.trim() || "Unspecified",
      batteryRangeKm: isElectricFuel(modelForm.fuelType)
        ? modelForm.batteryRangeKm
        : undefined,
      status: "Approved",
    });
    setMasterProfiles((current) => [profile, ...current]);
    setModelForm({
      make: "",
      model: "",
      year: "",
      fuelType: "Electric",
      color: "",
      batteryRangeKm: "",
    });
    setMessage("Master profile saved.");
  };

  const handleMasterProfileUpdate = async (
    profile: MasterCarProfileType,
    patch: Partial<MasterCarProfileType>
  ) => {
    const updated = await updateMasterProfile(profile._id, patch);
    setMasterProfiles((current) =>
      current.map((item) => (item._id === updated._id ? updated : item))
    );
  };

  const handleToggleDealer = async (
    dealer: DealerType,
    field: "trustedDealer" | "isActive"
  ) => {
    const updated = await updateDealer(dealer._id, {
      [field]: !dealer[field],
    });
    setDealers((current) =>
      current.map((item) => (item._id === updated._id ? updated : item))
    );
  };

  const handleSaveDealerMember = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!dealerMemberForm.dealer || !dealerMemberForm.userPhone.trim()) {
      setMessage("Choose a dealer and enter the app user's phone.");
      return;
    }

    try {
      const member = await createDealerMember({
        dealer: dealerMemberForm.dealer,
        userPhone: dealerMemberForm.userPhone.trim(),
        role: dealerMemberForm.role,
        canDirectPost: dealerMemberForm.canDirectPost,
        isActive: dealerMemberForm.isActive,
      });
      setDealerMembers((current) => [
        member,
        ...current.filter((item) => item._id !== member._id),
      ]);
      setDealerMemberForm({
        dealer: dealerMemberForm.dealer,
        userPhone: "",
        role: "Staff",
        canDirectPost: true,
        isActive: true,
      });
      setMessage("App account linked to importer account.");
    } catch (error: any) {
      setMessage(error?.response?.data?.msg || "Could not link app account.");
    }
  };

  const handleDealerMemberUpdate = async (
    member: DealerMemberType,
    patch: Partial<Pick<DealerMemberType, "role" | "canDirectPost" | "isActive">>
  ) => {
    const updated = await updateDealerMember(member._id, patch);
    setDealerMembers((current) =>
      current.map((item) => (item._id === updated._id ? updated : item))
    );
  };

  const handleDeleteDealerMember = async (member: DealerMemberType) => {
    if (!dashboardConfirm("Remove this app account from the importer account?")) {
      return;
    }
    await deleteDealerMember(member._id);
    setDealerMembers((current) =>
      current.filter((item) => item._id !== member._id)
    );
    setMessage("Importer app account link removed.");
  };

  const handleInventoryUpdate = async (
    item: DealerInventoryType,
    patch: Partial<DealerInventoryType> & { flagSuspicious?: boolean }
  ) => {
    const updated = await updateAdminDealerInventory(item._id, patch);
    setInventory((current) =>
      current.map((row) => (row._id === item._id ? updated : row))
    );
  };

  const getPriceInput = (price: AdminImporterPriceType) =>
    priceInputs[price._id] ?? Number(price.price || 0).toLocaleString("en-US");

  const handlePriceInput = (price: AdminImporterPriceType, value: string) => {
    const digits = value.replace(/[^0-9]/g, "");
    setPriceInputs((current) => ({
      ...current,
      [price._id]: digits ? Number(digits).toLocaleString("en-US") : "",
    }));
  };

  const handlePriceAction = async (
    price: AdminImporterPriceType,
    action: "confirm" | "update" | "unavailable" | "available"
  ) => {
    try {
      setSavingPriceId(price._id);
      const enteredPrice = Number(getPriceInput(price).replace(/,/g, ""));
      if ((action === "update" || action === "available") && enteredPrice <= 0) {
        setMessage("Enter a valid importer price.");
        return;
      }
      const updated = await updateAdminImporterPrice(price._id, {
        action,
        ...(action === "update" || action === "available"
          ? { price: enteredPrice }
          : {}),
        ...(price.lastConfirmedAt
          ? { expectedLastConfirmedAt: price.lastConfirmedAt }
          : {}),
      });
      setImporterPrices((current) =>
        current.map((item) => (item._id === updated._id ? updated : item))
      );
      setPriceInputs((current) => ({
        ...current,
        [updated._id]: Number(updated.price || 0).toLocaleString("en-US"),
      }));
      setMessage(
        action === "unavailable"
          ? "Car marked unavailable."
          : action === "confirm"
            ? "Price confirmed."
            : "Importer price updated."
      );
    } catch (error: any) {
      setMessage(error?.response?.data?.msg || "Could not update this price.");
    } finally {
      setSavingPriceId(null);
    }
  };

  const getPublishForm = (submission: DealerSubmissionType): PublishFormType => {
    const publishedCar =
      submission.publishedCar && typeof submission.publishedCar === "object"
        ? submission.publishedCar
        : undefined;
    const isDeal = Boolean(submission.isDeal || publishedCar?.isDeal);
    return publishForms[submission._id] || {
      make: "",
      model: submission.makeModelText || "",
      year: submission.year ? String(submission.year) : "",
      price: submission.price ? String(submission.price) : "",
      fuelType: submission.fuelType || "",
      color: submission.color || publishedCar?.color || "",
      batteryRangeKm: submission.batteryRangeKm
        ? String(submission.batteryRangeKm)
        : publishedCar?.batteryRangeKm
          ? String(publishedCar.batteryRangeKm)
          : "",
      isDeal,
      description: submission.note || "",
    };
  };

  const updatePublishForm = (
    submission: DealerSubmissionType,
    patch: Partial<PublishFormType>
  ) => {
    setPublishForms((current) => ({
      ...current,
      [submission._id]: { ...getPublishForm(submission), ...patch },
    }));
  };

  const handleSubmissionUpdate = async (
    submission: DealerSubmissionType,
    patch: { status?: DealerSubmissionStatusType; staffNotes?: string }
  ) => {
    const updated = await updateAdminDealerSubmission(submission._id, patch);
    setSubmissions((current) =>
      current.map((item) => (item._id === updated._id ? updated : item))
    );
    setMessage("Dealer submission updated.");
  };

  const handlePublishSubmission = async (
    event: React.FormEvent,
    submission: DealerSubmissionType
  ) => {
    event.preventDefault();
    try {
      const updated = await publishDealerSubmission(
        submission._id,
        getPublishForm(submission)
      );
      setSubmissions((current) =>
        current.map((item) => (item._id === updated._id ? updated : item))
      );
      setMessage("Dealer submission published.");
    } catch (error: any) {
      setMessage(error?.response?.data?.msg || "Could not publish submission.");
    }
  };

  const handleLeadUpdate = async (
    lead: DealerInventoryLeadType,
    patch: {
      status?: DealerLeadStatusType;
      notes?: string;
      commissionOutcome?: string;
    }
  ) => {
    const updated = await updateDealerLead(lead._id, patch);
    setLeads((current) =>
      current.map((item) => (item._id === lead._id ? updated : item))
    );
  };

  const getProfileMakeName = (make: MasterCarProfileType["make"]) => {
    if (typeof make !== "string") return make?.name || "Unknown make";
    return carMakes.find((item) => item._id === make)?.name || "Unknown make";
  };
  const getMemberDealer = (member: DealerMemberType) =>
    typeof member.dealer === "object" ? member.dealer : undefined;
  const getMemberUser = (member: DealerMemberType) =>
    typeof member.user === "object" ? member.user : undefined;
  const filteredSubmissions = useMemo(
    () =>
      submissionStatusFilter === "All"
        ? submissions
        : submissions.filter(
            (submission) =>
              getSubmissionStatusView(submission.status) ===
              submissionStatusFilter
          ),
    [submissionStatusFilter, submissions]
  );
  const submissionStatusCounts = useMemo(() => {
    const counts: Record<string, number> = { All: submissions.length };
    submissions.forEach((submission) => {
      const status = getSubmissionStatusView(submission.status);
      counts[status] = (counts[status] || 0) + 1;
    });
    return counts;
  }, [submissions]);
  const canSaveModel = Boolean(
    modelForm.make &&
      modelForm.model.trim() &&
      modelForm.year &&
      modelForm.fuelType
  );
  const voiceSearchSupported = Boolean(getSpeechRecognition());
  const overduePriceCount = importerPrices.filter(
    (price) => price.freshness === "overdue"
  ).length;
  const priceTabLabels: Record<TabType, string> = {
    prices: overduePriceCount ? `Price checks (${overduePriceCount})` : "Price checks",
    dealers: "Importers",
    inventory: "Inventory",
    submissions: "New car posts",
    leads: "Buyer requests",
    models: "Car models",
  };
  const primaryTabs: TabType[] = ["dealers", "prices", "submissions", "leads"];
  const moreTabActive = activeTab === "inventory" || activeTab === "models";
  const searchPlaceholders: Record<TabType, string> = {
    dealers: "Search importers by name or phone",
    prices: "Search cars that need a price update",
    submissions: "Search submitted new cars",
    leads: "Search buyer requests",
    inventory: "Search importer inventory",
    models: "Search car models",
  };

  return (
    <div className={CommonStyles.pageWrapper}>
      <Menu />
      <main className={ClassName.container}>
        <div className={ClassName.header}>
          <div>
            <h1 className={ClassName.title}>Importer management</h1>
            <p className={ClassName.description}>
              Manage importer accounts, price updates, car posts, and buyer requests.
            </p>
          </div>
          <div className={ClassName.headerActions}>
            <button className={ClassName.refreshButton} onClick={() => loadAll()}>
              Refresh
            </button>
          </div>
        </div>

        <form className={ClassName.searchRow} onSubmit={handleSearch}>
          <input
            className={ClassName.searchInput}
            value={searchValue}
            onChange={(event) => setSearchValue(event.target.value)}
            placeholder={searchPlaceholders[activeTab]}
          />
          {voiceSearchSupported && (
            <button
              type="button"
              className={`${ClassName.iconButton} ${
                listening ? ClassName.listeningButton : ""
              }`}
              aria-label="Search by voice"
              onClick={startVoiceSearch}
            >
              <IoMicOutline />
            </button>
          )}
          <button className={ClassName.iconButton} aria-label="Search">
            <IoSearchOutline />
          </button>
          <button
            type="button"
            className={ClassName.clearButton}
            onClick={() => {
              setSearchValue("");
              loadAll("");
            }}
            aria-label="Clear search"
          >
            <IoCloseOutline />
            <span>Clear</span>
          </button>
        </form>
        {inventorySuggestions.length > 0 &&
          (activeTab === "inventory" || activeTab === "models") && (
          <div className={ClassName.suggestionRow}>
            {inventorySuggestions.map((suggestion) => (
              <button
                key={suggestion}
                type="button"
                className={ClassName.suggestionChip}
                onClick={() => handleSuggestion(suggestion)}
              >
                {suggestion}
              </button>
            ))}
          </div>
        )}

        <div className={ClassName.tabs}>
          {primaryTabs.map((tab) => (
            <button
              key={tab}
              className={`${ClassName.tab} ${
                activeTab === tab ? ClassName.activeTab : ""
              }`}
              onClick={() => setActiveTab(tab)}
            >
              {priceTabLabels[tab]}
            </button>
          ))}
          <label
            className={`${ClassName.moreTab} ${
              moreTabActive ? ClassName.activeMoreTab : ""
            }`}
          >
            <span>{moreTabActive ? priceTabLabels[activeTab] : "More"}</span>
            <IoChevronDownOutline />
            <select
              aria-label="More dealer tools"
              value={moreTabActive ? activeTab : ""}
              onChange={(event) => {
                if (event.target.value) {
                  setActiveTab(event.target.value as TabType);
                }
              }}
            >
              <option value="">More</option>
              <option value="inventory">Inventory</option>
              <option value="models">Car models</option>
            </select>
          </label>
        </div>

        {message && <p className={ClassName.message}>{message}</p>}
        {pageErrors.length > 0 && (
          <div className={ClassName.errorBox}>
            {pageErrors.map((error) => (
              <p key={error}>{error}</p>
            ))}
          </div>
        )}
        {loading && <div className="primaryLoader"></div>}
        {!loading && activeTab === "prices" && (
          <section className={ClassName.section}>
            <div className={ClassName.priceQueueHeader}>
              <div>
                <h2 className={ClassName.cardTitle}>New-car price checks</h2>
                <p className={ClassName.meta}>
                  Yellow means 3+ days without confirmation. Red means 7+ days.
                </p>
              </div>
              <span className={ClassName.queueCount}>{importerPrices.length} cars</span>
            </div>
            {importerPrices.length === 0 && (
              <p className={ClassName.emptyText}>No importer prices found.</p>
            )}
            <div className={ClassName.priceGrid}>
              {importerPrices.map((price) => {
                const isSaving = savingPriceId === price._id;
                const freshnessClass =
                  price.freshness === "overdue"
                    ? ClassName.overduePrice
                    : price.freshness === "warning"
                      ? ClassName.warningPrice
                      : price.freshness === "unavailable"
                        ? ClassName.unavailablePrice
                        : "";
                return (
                  <article
                    key={price._id}
                    className={`${ClassName.priceCard} ${freshnessClass}`}
                  >
                    {price.imageUrl ? (
                      <img
                        className={ClassName.priceImage}
                        src={price.imageUrl}
                        alt={`${price.year || ""} ${price.make} ${price.model}`.trim()}
                      />
                    ) : (
                      <div className={ClassName.priceImagePlaceholder}>No photo</div>
                    )}
                    <div className={ClassName.priceBody}>
                      <div className={ClassName.rowBetween}>
                        <div>
                          <h3 className={ClassName.cardTitle}>
                            {[price.year, price.make, price.model].filter(Boolean).join(" ")}
                          </h3>
                          <p className={ClassName.meta}>
                            {[price.color, price.fuelType].filter(Boolean).join(" • ")}
                          </p>
                        </div>
                        <span className={ClassName.freshnessLabel}>
                          {price.freshness === "unavailable"
                            ? "Unavailable"
                            : price.ageDays === null
                              ? "Never confirmed"
                              : price.ageDays === 0
                                ? "Confirmed today"
                                : `${price.ageDays} days ago`}
                        </span>
                      </div>
                      <div className={ClassName.importerLine}>
                        <span>{price.dealerName || "Unknown importer"}</span>
                        <PhoneLink phone={price.dealerPhone} />
                      </div>
                      <div className={ClassName.priceControls}>
                        <input
                          className={ClassName.priceInput}
                          inputMode="numeric"
                          value={getPriceInput(price)}
                          onChange={(event) => handlePriceInput(price, event.target.value)}
                          aria-label={`Price for ${price.make} ${price.model}`}
                        />
                        {price.freshness === "unavailable" ? (
                          <button
                            type="button"
                            className={ClassName.darkButton}
                            disabled={isSaving}
                            onClick={() => handlePriceAction(price, "available")}
                          >
                            Make available
                          </button>
                        ) : (
                          <>
                            <button
                              type="button"
                              className={ClassName.darkButton}
                              disabled={isSaving}
                              onClick={() => handlePriceAction(price, "update")}
                            >
                              Save price
                            </button>
                            <button
                              type="button"
                              className={ClassName.softButton}
                              disabled={isSaving}
                              onClick={() => handlePriceAction(price, "confirm")}
                            >
                              Still correct
                            </button>
                            <button
                              type="button"
                              className={ClassName.textButton}
                              disabled={isSaving}
                              onClick={() => handlePriceAction(price, "unavailable")}
                            >
                              Unavailable
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          </section>
        )}
        {!loading && activeTab === "dealers" && (
          <section className={ClassName.section}>
            <div className={ClassName.sectionHeader}>
              <div>
                <h2 className={ClassName.sectionTitle}>Importer accounts</h2>
                <p className={ClassName.meta}>
                  {dealers.filter((dealer) => dealer.isActive).length} active importers ·{" "}
                  {dealers.filter((dealer) => dealer.portalRole === "Admin").length} portal admins
                </p>
              </div>
              <div className={ClassName.sectionActions}>
                <button
                  type="button"
                  className={ClassName.softActionButton}
                  onClick={() => setMemberPanelOpen((current) => !current)}
                >
                  {memberPanelOpen ? "Hide app links" : "Link app user"}
                </button>
                <button
                  type="button"
                  className={ClassName.darkActionButton}
                  onClick={handleAddDealer}
                >
                  <IoAddOutline />
                  Add importer
                </button>
              </div>
            </div>

            {dealerFormOpen && (
              <form
                className={ClassName.formPanel}
                onSubmit={handleSaveDealer}
                ref={dealerFormRef}
              >
                <div className={ClassName.formHeader}>
                  <div>
                    <h3 className={ClassName.cardTitle}>
                      {editingDealerId ? `Edit ${dealerForm.name}` : "New importer"}
                    </h3>
                    <p className={ClassName.meta}>
                      Only name, phone, and password are required.
                    </p>
                  </div>
                  <button type="button" onClick={resetDealerForm}>
                    <IoCloseOutline />
                    Close
                  </button>
                </div>

                <div className={ClassName.basicFormGrid}>
                  <label className={ClassName.field}>
                    <span>Importer name</span>
                    <input
                      className={ClassName.input}
                      placeholder="Company or importer name"
                      value={dealerForm.name}
                      onChange={(event) =>
                        setDealerForm({ ...dealerForm, name: event.target.value })
                      }
                      required
                    />
                  </label>
                  <label className={ClassName.field}>
                    <span>Phone number</span>
                    <input
                      className={ClassName.input}
                      placeholder="09..."
                      value={dealerForm.phoneNumber}
                      onChange={(event) =>
                        setDealerForm({
                          ...dealerForm,
                          phoneNumber: event.target.value,
                        })
                      }
                      required
                    />
                  </label>
                  <label className={ClassName.field}>
                    <span>{editingDealerId ? "New password" : "Password"}</span>
                    <input
                      className={ClassName.input}
                      placeholder={editingDealerId ? "Leave blank to keep it" : "Create a password"}
                      value={dealerForm.password}
                      onChange={(event) =>
                        setDealerForm({ ...dealerForm, password: event.target.value })
                      }
                      required={!editingDealerId}
                      type="password"
                    />
                  </label>
                </div>

                <button
                  className={ClassName.advancedToggle}
                  type="button"
                  aria-expanded={dealerAdvancedOpen}
                  onClick={() => setDealerAdvancedOpen((current) => !current)}
                >
                  <span>More options</span>
                  <IoChevronDownOutline
                    className={dealerAdvancedOpen ? "rotate-180" : ""}
                  />
                </button>

                {dealerAdvancedOpen && (
                  <div className={ClassName.advancedGrid}>
                    <label className={ClassName.field}>
                      <span>Email (optional)</span>
                      <input
                        className={ClassName.input}
                        placeholder="name@example.com"
                        value={dealerForm.email}
                        onChange={(event) =>
                          setDealerForm({ ...dealerForm, email: event.target.value })
                        }
                      />
                    </label>
                    <label className={ClassName.field}>
                      <span>Account access</span>
                      <select
                        className={ClassName.input}
                        value={dealerForm.portalRole}
                        onChange={(event) =>
                          setDealerForm({
                            ...dealerForm,
                            portalRole: event.target.value as "Importer" | "Admin",
                          })
                        }
                      >
                        <option value="Importer">Importer · manages own cars</option>
                        <option value="Admin">Portal admin · manages all importers</option>
                      </select>
                    </label>
                    <label className={ClassName.field}>
                      <span>Internal notes (optional)</span>
                      <input
                        className={ClassName.input}
                        placeholder="Visible to BossCar staff only"
                        value={dealerForm.notes}
                        onChange={(event) =>
                          setDealerForm({ ...dealerForm, notes: event.target.value })
                        }
                      />
                    </label>
                    <div className={ClassName.optionRow}>
                      <label className={ClassName.checkbox}>
                        <input
                          type="checkbox"
                          checked={dealerForm.trustedDealer}
                          onChange={(event) =>
                            setDealerForm({
                              ...dealerForm,
                              trustedDealer: event.target.checked,
                            })
                          }
                        />
                        Trusted importer
                      </label>
                      <label className={ClassName.checkbox}>
                        <input
                          type="checkbox"
                          checked={dealerForm.isActive}
                          onChange={(event) =>
                            setDealerForm({
                              ...dealerForm,
                              isActive: event.target.checked,
                            })
                          }
                        />
                        Account active
                      </label>
                    </div>
                  </div>
                )}

                {dealerFormError && (
                  <p className={ClassName.formError}>{dealerFormError}</p>
                )}
                <div className={ClassName.formFooter}>
                  <button type="button" onClick={resetDealerForm}>
                    Cancel
                  </button>
                  <button
                    className={`${ClassName.darkActionButton} ${
                      dealerSaving ? ClassName.disabledButton : ""
                    }`}
                    disabled={dealerSaving}
                    type="submit"
                  >
                    {dealerSaving
                      ? editingDealerId
                        ? "Saving..."
                        : "Creating..."
                      : editingDealerId
                      ? "Save changes"
                      : "Create importer"}
                  </button>
                </div>
              </form>
            )}

            {memberPanelOpen && (
              <div className={ClassName.linkPanel}>
                <div>
                  <h2 className={ClassName.cardTitle}>Linked BossCar app users</h2>
                  <p className={ClassName.meta}>
                    Give an existing BossCar app user permission to post for an importer.
                  </p>
                </div>
                <form className={ClassName.memberForm} onSubmit={handleSaveDealerMember}>
                <select
                  className={ClassName.input}
                  value={dealerMemberForm.dealer}
                  onChange={(event) =>
                    setDealerMemberForm({
                      ...dealerMemberForm,
                      dealer: event.target.value,
                    })
                  }
                >
                  <option value="">Choose importer</option>
                  {dealers.map((dealer) => (
                    <option key={dealer._id} value={dealer._id}>
                      {dealer.name} {dealer.phoneNumber ? `(${dealer.phoneNumber})` : ""}
                    </option>
                  ))}
                </select>
                <input
                  className={ClassName.input}
                  placeholder="App user phone"
                  value={dealerMemberForm.userPhone}
                  onChange={(event) =>
                    setDealerMemberForm({
                      ...dealerMemberForm,
                      userPhone: event.target.value,
                    })
                  }
                />
                <select
                  className={ClassName.input}
                  value={dealerMemberForm.role}
                  onChange={(event) =>
                    setDealerMemberForm({
                      ...dealerMemberForm,
                      role: event.target.value as DealerMemberRoleType,
                    })
                  }
                >
                  <option value="Staff">Staff</option>
                  <option value="Owner">Owner</option>
                </select>
                <label className={ClassName.checkbox}>
                  <input
                    type="checkbox"
                    checked={dealerMemberForm.canDirectPost}
                    onChange={(event) =>
                      setDealerMemberForm({
                        ...dealerMemberForm,
                        canDirectPost: event.target.checked,
                      })
                    }
                  />
                  Can post new cars
                </label>
                <label className={ClassName.checkbox}>
                  <input
                    type="checkbox"
                    checked={dealerMemberForm.isActive}
                    onChange={(event) =>
                      setDealerMemberForm({
                        ...dealerMemberForm,
                        isActive: event.target.checked,
                      })
                    }
                  />
                  Active link
                </label>
                <button className={ClassName.primaryButton}>Link account</button>
                </form>
                <div className={ClassName.memberList}>
                {dealerMembers.length === 0 && (
                  <p className={ClassName.emptyText}>No linked app accounts yet.</p>
                )}
                {dealerMembers.map((member) => {
                  const dealer = getMemberDealer(member);
                  const linkedUser = getMemberUser(member);
                  return (
                    <article key={member._id} className={ClassName.memberCard}>
                      <div>
                        <h3 className={ClassName.cardTitle}>
                          {linkedUser?.name || linkedUser?.phoneNumber || "App user"}
                        </h3>
                        <p className={ClassName.meta}>
                          App: {linkedUser?.phoneNumber || "No phone"} | Importer:{" "}
                          {dealer?.name || "Unknown"} | {member.role}
                        </p>
                      </div>
                      <div className={ClassName.actions}>
                        <button
                          type="button"
                          onClick={() =>
                            handleDealerMemberUpdate(member, {
                              canDirectPost: !member.canDirectPost,
                            })
                          }
                        >
                          {member.canDirectPost ? "Pause posting" : "Allow posting"}
                        </button>
                        <button
                          type="button"
                          onClick={() =>
                            handleDealerMemberUpdate(member, {
                              isActive: !member.isActive,
                            })
                          }
                        >
                          {member.isActive ? "Deactivate link" : "Activate link"}
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDeleteDealerMember(member)}
                        >
                          Remove
                        </button>
                      </div>
                    </article>
                  );
                })}
                </div>
              </div>
            )}
            <div className={ClassName.list}>
              {dealers.length === 0 && (
                <p className={ClassName.emptyText}>No dealers found.</p>
              )}
              {dealers.map((dealer) => (
                <article key={dealer._id} className={ClassName.card}>
                  <div>
                    <div className={ClassName.dealerHeading}>
                      <h2 className={ClassName.cardTitle}>{dealer.name}</h2>
                      <span className={ClassName.accountTypeBadge}>
                        {dealer.portalRole === "Admin"
                          ? "Portal admin"
                          : "Importer"}
                      </span>
                      <span
                        className={
                          dealer.isActive
                            ? ClassName.activeStatusBadge
                            : ClassName.inactiveStatusBadge
                        }
                      >
                        {dealer.isActive ? "Active" : "Inactive"}
                      </span>
                      {dealer.trustedDealer && (
                        <span className={ClassName.trustedBadge}>Trusted</span>
                      )}
                    </div>
                    <p className={ClassName.meta}>
                      <PhoneLink phone={dealer.phoneNumber} /> |{" "}
                      {dealer.email || "No email"}
                    </p>
                  </div>
                  <div className={ClassName.dealerActions}>
                    <button
                      type="button"
                      className={ClassName.darkActionButton}
                      onClick={() => handleEditDealer(dealer)}
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      className={ClassName.softActionButton}
                      onClick={() =>
                        handleToggleDealer(dealer, "trustedDealer")
                      }
                    >
                      {dealer.trustedDealer ? "Untrust" : "Trust"}
                    </button>
                    <button
                      type="button"
                      className={ClassName.softActionButton}
                      onClick={() => handleToggleDealer(dealer, "isActive")}
                    >
                      {dealer.isActive ? "Deactivate" : "Activate"}
                    </button>
                  </div>
                </article>
              ))}
            </div>
          </section>
        )}

        {!loading && activeTab === "inventory" && (
          <section className={ClassName.list}>
            {inventory.length === 0 && (
              <p className={ClassName.emptyText}>No dealer cars found.</p>
            )}
            {inventory.map((item) => (
              <article key={item._id} className={ClassName.card}>
                <div>
                  <h2 className={ClassName.cardTitle}>
                    {item.year} {item.make} {item.model}
                  </h2>
                  <p className={ClassName.meta}>
                    {[
                      item.color,
                      item.fuelType,
                      isBatteryFuel(item.fuelType) &&
                        (item.batteryRangeKm || item.kmRange),
                      `${Number(item.price).toLocaleString()} Birr`,
                    ]
                      .filter(Boolean)
                      .join(" | ")}
                  </p>
                  <p className={ClassName.meta}>
                    Dealer: {item.dealer?.name || "Unknown"} | Phone:{" "}
                    <PhoneLink phone={item.dealer?.phoneNumber} /> |{" "}
                    {item.approvalStatus} | score {item.qualityScore}
                  </p>
                  {item.publicVisibility?.reason && (
                    <p className={ClassName.meta}>
                      Public: {item.publicVisibility.status} |{" "}
                      {item.publicVisibility.reason}
                    </p>
                  )}
                  {!!item.suspiciousFlags?.length && (
                    <p className={ClassName.warning}>
                      Flags: {item.suspiciousFlags.join(", ")}
                    </p>
                  )}
                </div>
                <div className={ClassName.actions}>
                  <button
                    onClick={() =>
                      handleInventoryUpdate(item, {
                        approvalStatus: "Approved",
                      })
                    }
                  >
                    Approve
                  </button>
                  <button
                    onClick={() =>
                      handleInventoryUpdate(item, {
                        approvalStatus: "Rejected",
                      })
                    }
                  >
                    Reject
                  </button>
                  <button
                    onClick={() =>
                      handleInventoryUpdate(item, { flagSuspicious: true })
                    }
                  >
                    Flag suspicious
                  </button>
                </div>
              </article>
            ))}
          </section>
        )}

        {!loading && activeTab === "submissions" && (
          <section className={ClassName.list}>
            <div className={ClassName.filterRow}>
              {adminSubmissionFilters.map((status) => (
                <button
                  key={status}
                  className={`${ClassName.filterChip} ${
                    submissionStatusFilter === status
                      ? ClassName.activeFilterChip
                      : ""
                  }`}
                  type="button"
                  onClick={() => setSubmissionStatusFilter(status)}
                >
                  {status}
                  <span className={ClassName.filterCount}>
                    {submissionStatusCounts[status] || 0}
                  </span>
                </button>
              ))}
            </div>
            {submissions.length === 0 && (
              <p className={ClassName.emptyText}>No dealer submissions found.</p>
            )}
            {submissions.length > 0 && filteredSubmissions.length === 0 && (
              <p className={ClassName.emptyText}>
                No dealer submissions match this status.
              </p>
            )}
            {filteredSubmissions.map((submission) => {
              const form = getPublishForm(submission);
              const dealer =
                typeof submission.dealer === "string"
                  ? undefined
                  : submission.dealer;
              const isOpen = openSubmissionId === submission._id;
              return (
                <article key={submission._id} className={ClassName.submissionCard}>
                  <div className={ClassName.submissionPhotos}>
                    {getSortedSubmissionImages(submission).map((image, index) => (
                      <div
                        className={ClassName.submissionThumb}
                        key={image._id || `${image.slot}-${index}`}
                      >
                        <img
                          className={ClassName.fitImage}
                          src={image.url}
                          alt={image.slot}
                        />
                        {image.slot === "extra" && (
                          <span className={ClassName.photoBadge}>Extra</span>
                        )}
                        {image.slot === "deal" && (
                          <span className={ClassName.photoBadge}>Deal</span>
                        )}
                      </div>
                    ))}
                    {submissionImageSlots
                      .filter(
                        (slot) =>
                          !submission.images?.some((item) => item.slot === slot)
                      )
                      .map((slot) => (
                        <div className={ClassName.submissionThumb} key={slot}>
                          <span>{slot}</span>
                        </div>
                      ))}
                  </div>
                  <div className={ClassName.submissionBody}>
                    <div className={ClassName.rowBetween}>
                      <div>
                        <h2 className={ClassName.cardTitle}>
                          {submission.makeModelText || "Dealer submitted car"}
                        </h2>
                        <p className={ClassName.meta}>
                          Dealer: {dealer?.name || "Unknown"} | Phone:{" "}
                          <PhoneLink phone={dealer?.phoneNumber} /> |{" "}
                          {getSubmissionStatusView(submission.status)}
                        </p>
                        <p className={ClassName.meta}>
                          {[
                            submission.year,
                            submission.fuelType,
                            submission.price
                              ? `${Number(submission.price).toLocaleString()} Birr`
                              : "",
                          ]
                            .filter(Boolean)
                            .join(" | ")}
                        </p>
                        {submission.note && (
                          <p className={ClassName.meta}>{submission.note}</p>
                        )}
                      </div>
                      <select
                        className={ClassName.select}
                        value={getSubmissionStatusView(submission.status)}
                        onChange={(event) =>
                          handleSubmissionUpdate(submission, {
                            status: getBackendStatusFromView(
                              event.target.value as Exclude<
                                SubmissionStatusFilterType,
                                "All"
                              >
                            ),
                          })
                        }
                      >
                        <option>Review</option>
                        <option disabled={!submission.publishedCar}>Posted</option>
                        <option>Unavailable</option>
                        <option>Rejected</option>
                      </select>
                    </div>
                    <div className={ClassName.actions}>
                      <button
                        className={ClassName.primaryTextButton}
                        type="button"
                        onClick={() =>
                          setOpenSubmissionId(isOpen ? null : submission._id)
                        }
                      >
                        {isOpen ? "Hide details" : "Complete details"}
                      </button>
                      {submission.publishedCar && (
                        <span className={ClassName.successPill}>Published</span>
                      )}
                    </div>
                    <input
                      className={ClassName.noteInput}
                      placeholder="Staff note for dealer"
                      defaultValue={submission.staffNotes || ""}
                      onBlur={(event) =>
                        handleSubmissionUpdate(submission, {
                          staffNotes: event.target.value,
                        })
                      }
                    />
                    {isOpen && (
                      <form
                        className={ClassName.publishForm}
                        onSubmit={(event) =>
                          handlePublishSubmission(event, submission)
                        }
                      >
                      <select
                        className={ClassName.input}
                        value={form.make}
                        onChange={(event) =>
                          updatePublishForm(submission, {
                            make: event.target.value,
                          })
                        }
                        required
                      >
                        <option value="">Car brand</option>
                        {carMakes.map((make) => (
                          <option key={make._id} value={make._id}>
                            {make.name}
                          </option>
                        ))}
                      </select>
                      <input
                        className={ClassName.input}
                        placeholder="Model"
                        value={form.model}
                        onChange={(event) =>
                          updatePublishForm(submission, {
                            model: event.target.value,
                          })
                        }
                        required
                      />
                      <input
                        className={ClassName.input}
                        placeholder="Year"
                        inputMode="numeric"
                        value={form.year}
                        onChange={(event) =>
                          updatePublishForm(submission, {
                            year: event.target.value,
                          })
                        }
                        required
                      />
                      <input
                        className={ClassName.input}
                        placeholder="Price"
                        inputMode="numeric"
                        value={form.price}
                        onChange={(event) =>
                          updatePublishForm(submission, {
                            price: event.target.value,
                          })
                        }
                        required
                      />
                      <select
                        className={ClassName.input}
                        value={form.fuelType}
                        onChange={(event) =>
                          updatePublishForm(submission, {
                            fuelType: event.target.value,
                            batteryRangeKm:
                              event.target.value === "Electric"
                                ? form.batteryRangeKm
                                : "",
                          })
                        }
                        required
                      >
                        <option value="">Fuel</option>
                        {fuelOptions.map((fuel) => (
                          <option key={fuel}>{fuel}</option>
                        ))}
                      </select>
                      <input
                        className={ClassName.input}
                        placeholder="Color"
                        value={form.color}
                        onChange={(event) =>
                          updatePublishForm(submission, {
                            color: event.target.value,
                          })
                        }
                      />
                      {isElectricFuel(form.fuelType) && (
                        <input
                          className={ClassName.input}
                          placeholder="KM range per charge"
                          inputMode="numeric"
                          value={form.batteryRangeKm}
                          onChange={(event) =>
                            updatePublishForm(submission, {
                              batteryRangeKm: event.target.value.replace(/\D/g, ""),
                            })
                          }
                        />
                      )}
                      <textarea
                        className={`${ClassName.input} ${ClassName.publishTextarea}`}
                        placeholder="Description / note"
                        value={form.description}
                        onChange={(event) =>
                          updatePublishForm(submission, {
                            description: event.target.value,
                          })
                        }
                      />
                      <div className="md:col-span-2 xl:col-span-4 rounded-2xl border border-borderPrimary bg-bgSecondary/40 p-3">
                        <label className="flex items-center gap-2 text-sm font-semibold text-primaryText">
                          <input
                            type="checkbox"
                            checked={form.isDeal}
                            onChange={(event) =>
                              updatePublishForm(submission, {
                                isDeal: event.target.checked,
                              })
                            }
                          />
                          Show in Deals portal
                        </label>
                        {form.isDeal && (
                          <p className="mt-2 text-sm text-secondaryText">
                            This car will appear in Deals for 48 hours after publish, then remain as a normal marketplace post.
                          </p>
                        )}
                      </div>
                        <button type="submit" className={ClassName.primaryButton}>
                          Publish app, web & prices
                        </button>
                      </form>
                    )}
                  </div>
                </article>
              );
            })}
          </section>
        )}

        {!loading && activeTab === "models" && (
          <section className={ClassName.section}>
            {carMakes.length === 0 && (
              <p className={ClassName.emptyText}>
                No car brands loaded yet. Refresh after the API is available.
              </p>
            )}
            <form className={ClassName.form} onSubmit={handleCreateMasterProfile}>
              <select
                className={ClassName.input}
                value={modelForm.make}
                onChange={(event) =>
                  setModelForm({ ...modelForm, make: event.target.value })
                }
                disabled={carMakes.length === 0}
                required
              >
                <option value="">Select make</option>
                {carMakes.map((make) => (
                  <option key={make._id} value={make._id}>
                    {make.name}
                  </option>
                ))}
              </select>
              <input
                className={ClassName.input}
                placeholder="Model name"
                value={modelForm.model}
                onChange={(event) =>
                  setModelForm({ ...modelForm, model: event.target.value })
                }
                required
              />
              <input
                className={ClassName.input}
                placeholder="Year"
                inputMode="numeric"
                value={modelForm.year}
                onChange={(event) =>
                  setModelForm({
                    ...modelForm,
                    year: event.target.value.replace(/\D/g, ""),
                  })
                }
                required
              />
              <select
                className={ClassName.input}
                value={modelForm.fuelType}
                onChange={(event) =>
                  setModelForm({
                    ...modelForm,
                    fuelType: event.target.value,
                    batteryRangeKm:
                      event.target.value === "Electric"
                        ? modelForm.batteryRangeKm
                        : "",
                  })
                }
                required
              >
                {fuelOptions.map((fuel) => (
                  <option key={fuel}>{fuel}</option>
                ))}
              </select>
              <input
                className={ClassName.input}
                placeholder="Color"
                value={modelForm.color}
                onChange={(event) =>
                  setModelForm({ ...modelForm, color: event.target.value })
                }
              />
              {isElectricFuel(modelForm.fuelType) && (
                <input
                  className={ClassName.input}
                  placeholder="KM range per charge"
                  inputMode="numeric"
                  value={modelForm.batteryRangeKm}
                  onChange={(event) =>
                    setModelForm({
                      ...modelForm,
                      batteryRangeKm: event.target.value.replace(/\D/g, ""),
                    })
                  }
                />
              )}
              <button
                className={`${ClassName.primaryButton} ${
                  !canSaveModel ? ClassName.disabledButton : ""
                }`}
                disabled={!canSaveModel}
              >
                Save model
              </button>
            </form>
            <div className={ClassName.list}>
              {masterProfiles.length === 0 && (
                <p className={ClassName.emptyText}>No master profiles found.</p>
              )}
              {masterProfiles.map((profile) => (
                <article key={profile._id} className={ClassName.card}>
                  <div>
                    <h2 className={ClassName.cardTitle}>
                      {getProfileMakeName(profile.make)} {profile.model}{" "}
                      {profile.year}
                    </h2>
                    <p className={ClassName.meta}>
                      {profile.fuelType} | {profile.color || "Unspecified"} |{" "}
                      {profile.status}
                      {profile.batteryRangeKm
                        ? ` | ${profile.batteryRangeKm} km/charge`
                        : ""}
                    </p>
                    {profile.overrideActive && (
                      <p className={ClassName.warning}>
                        Override: {profile.overridePrice?.toLocaleString()} Birr
                      </p>
                    )}
                  </div>
                  <div className={ClassName.actions}>
                    <button
                      onClick={() =>
                        handleMasterProfileUpdate(profile, {
                          status: "Approved",
                        })
                      }
                    >
                      Approve
                    </button>
                    <button
                      onClick={() =>
                        handleMasterProfileUpdate(profile, {
                          status: "Rejected",
                        })
                      }
                    >
                      Reject
                    </button>
                    <button
                      onClick={() =>
                        handleMasterProfileUpdate(profile, {
                          status: profile.status === "Hidden" ? "Approved" : "Hidden",
                        })
                      }
                    >
                      {profile.status === "Hidden" ? "Show" : "Hide"}
                    </button>
                  </div>
                </article>
              ))}
            </div>
          </section>
        )}

        {!loading && activeTab === "leads" && (
          <section className={ClassName.list}>
            {leads.length === 0 && (
              <p className={ClassName.emptyText}>No ordered-car leads found.</p>
            )}
            {leads.map((lead) => (
              <article
                id={`lead-${lead._id}`}
                key={lead._id}
                className={`${ClassName.card} ${
                  selectedLeadId === lead._id ? "ring-2 ring-red-300" : ""
                }`}
              >
                <div>
                  <h2 className={ClassName.cardTitle}>
                    {lead.inventory?.make} {lead.inventory?.model}
                  </h2>
                  <p className={ClassName.meta}>
                    Dealer: {lead.dealer?.name} | Dealer phone:{" "}
                    <PhoneLink phone={lead.dealer?.phoneNumber} /> | Buyer:{" "}
                    {lead.buyer?.name || lead.buyerPhone || "Unknown"}
                  </p>
                  <p className={ClassName.meta}>
                    {new Date(lead.createdAt).toLocaleString()}
                  </p>
                </div>
                <div className={ClassName.actions}>
                  <select
                    className={ClassName.select}
                    value={lead.status}
                    onChange={(event) =>
                      handleLeadUpdate(lead, {
                        status: event.target.value as DealerLeadStatusType,
                      })
                    }
                  >
                    <option>New</option>
                    <option>Contacted dealer</option>
                    <option>Buyer sent</option>
                    <option>Won</option>
                    <option>Lost</option>
                  </select>
                  <input
                    className={ClassName.noteInput}
                    placeholder="Commission / notes"
                    defaultValue={lead.commissionOutcome || lead.notes || ""}
                    onBlur={(event) =>
                      handleLeadUpdate(lead, {
                        commissionOutcome: event.target.value,
                      })
                    }
                  />
                </div>
              </article>
            ))}
          </section>
        )}
      </main>
    </div>
  );
}

const ClassName = {
  container:
    "mt-12 xl:mt-0 flex flex-col flex-1 bg-white shadow-primaryShadow p-4 sm:p-6 gap-4 overflow-auto",
  header: "flex flex-col sm:flex-row sm:items-center justify-between gap-3",
  headerActions: "flex flex-wrap items-center gap-2",
  title: "text-2xl font-bold text-primaryText",
  description: "text-sm text-secondaryText max-w-2xl",
  refreshButton: "rounded-xl bg-bgSecondary px-4 py-2 text-sm font-semibold",
  searchRow:
    "grid grid-cols-[1fr_auto_auto] sm:grid-cols-[1fr_auto_auto_auto] gap-2 sm:gap-3",
  searchInput:
    "h-11 rounded-xl border border-borderPrimary px-3 outline-none focus:border-primary",
  iconButton:
    "h-11 w-11 rounded-xl bg-primary text-white font-semibold flex items-center justify-center text-xl",
  listeningButton: "bg-primaryText",
  clearButton:
    "h-11 rounded-xl bg-bgSecondary px-3 sm:px-4 text-sm font-semibold flex items-center justify-center gap-1",
  suggestionRow: "flex flex-wrap gap-2",
  suggestionChip:
    "rounded-full bg-bgSecondary px-3 py-1 text-xs font-semibold text-primaryText",
  tabs: "flex gap-2 flex-wrap",
  tab: "rounded-xl px-4 py-2 text-sm bg-bgSecondary capitalize",
  activeTab: "bg-primary text-white",
  moreTab:
    "relative rounded-xl px-4 py-2 text-sm bg-bgSecondary flex items-center gap-2 overflow-hidden [&>select]:absolute [&>select]:inset-0 [&>select]:h-full [&>select]:w-full [&>select]:cursor-pointer [&>select]:opacity-0",
  activeMoreTab: "bg-primary text-white",
  filterRow: "flex gap-2 overflow-x-auto pb-1",
  filterChip:
    "flex shrink-0 items-center gap-2 rounded-xl bg-bgSecondary px-4 py-2 text-sm font-semibold",
  activeFilterChip: "bg-primary text-white",
  filterCount:
    "rounded-full bg-white/70 px-2 py-0.5 text-[11px] font-bold text-primaryText",
  message: "text-sm text-green-700",
  errorBox:
    "rounded-xl border border-primary bg-red-50 px-3 py-2 text-sm text-primary",
  emptyText: "text-sm text-secondaryText",
  priceQueueHeader:
    "rounded-2xl bg-bgSecondary p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-2",
  queueCount:
    "self-start sm:self-auto rounded-full bg-white px-3 py-1 text-xs font-bold text-primaryText",
  priceGrid: "grid grid-cols-1 2xl:grid-cols-2 gap-3",
  priceCard:
    "rounded-2xl border border-borderPrimary bg-white p-3 grid grid-cols-[76px_1fr] sm:grid-cols-[96px_1fr] gap-3",
  warningPrice: "border-amber-400 bg-amber-50",
  overduePrice: "border-red-500 bg-red-50",
  unavailablePrice: "opacity-75 bg-bgSecondary",
  priceImage:
    "h-20 w-[76px] sm:h-24 sm:w-24 rounded-xl object-cover bg-bgSecondary",
  priceImagePlaceholder:
    "h-20 w-[76px] sm:h-24 sm:w-24 rounded-xl bg-bgSecondary text-[11px] text-secondaryText flex items-center justify-center",
  priceBody: "min-w-0 flex flex-col gap-2",
  freshnessLabel:
    "shrink-0 rounded-full bg-white px-2 py-1 text-[11px] font-semibold text-secondaryText",
  importerLine:
    "flex flex-wrap items-center gap-x-3 gap-y-1 text-sm font-semibold text-primaryText",
  priceControls: "flex flex-wrap items-center gap-2",
  priceInput:
    "h-10 min-w-0 w-36 rounded-xl border border-borderPrimary bg-white px-3 font-semibold outline-none focus:border-primaryText",
  darkButton:
    "h-10 rounded-xl bg-primaryText px-3 text-xs font-bold text-white disabled:opacity-50",
  softButton:
    "h-10 rounded-xl bg-white border border-borderPrimary px-3 text-xs font-bold text-primaryText disabled:opacity-50",
  textButton:
    "h-10 px-2 text-xs font-semibold text-secondaryText hover:text-primary disabled:opacity-50",
  section: "flex flex-col gap-4",
  sectionHeader:
    "rounded-2xl bg-bgSecondary p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3",
  sectionTitle: "text-lg font-bold text-primaryText",
  sectionActions: "flex flex-wrap items-center gap-2",
  darkActionButton:
    "min-h-[40px] rounded-xl bg-primaryText px-4 py-2 text-sm font-bold text-white flex items-center justify-center gap-2 disabled:opacity-50",
  softActionButton:
    "min-h-[40px] rounded-xl border border-borderPrimary bg-white px-4 py-2 text-sm font-semibold text-primaryText",
  formPanel:
    "scroll-mt-4 rounded-2xl border border-borderPrimary bg-white p-4 flex flex-col gap-4 shadow-sm",
  formHeader:
    "flex items-start justify-between gap-3 [&>button]:flex [&>button]:items-center [&>button]:gap-1 [&>button]:rounded-xl [&>button]:bg-bgSecondary [&>button]:px-3 [&>button]:py-2 [&>button]:text-sm [&>button]:font-semibold",
  basicFormGrid: "grid grid-cols-1 md:grid-cols-3 gap-3",
  field:
    "flex flex-col gap-1.5 text-xs font-semibold text-secondaryText [&>span]:px-1",
  advancedToggle:
    "self-start flex items-center gap-2 rounded-xl bg-bgSecondary px-4 py-2 text-sm font-semibold text-primaryText [&>svg]:transition-transform",
  advancedGrid:
    "grid grid-cols-1 md:grid-cols-3 gap-3 rounded-2xl bg-bgSecondary p-3",
  optionRow: "flex flex-wrap items-end gap-4",
  formFooter:
    "flex items-center justify-end gap-2 border-t border-borderPrimary pt-3 [&>button:first-child]:rounded-xl [&>button:first-child]:px-4 [&>button:first-child]:py-2 [&>button:first-child]:text-sm [&>button:first-child]:font-semibold [&>button:first-child]:text-secondaryText",
  form:
    "grid grid-cols-1 md:grid-cols-3 xl:grid-cols-6 gap-3 scroll-mt-4",
  editingBanner:
    "md:col-span-3 xl:col-span-6 rounded-2xl bg-primaryText px-4 py-3 text-white flex flex-col sm:flex-row sm:items-center justify-between gap-3 [&>div]:flex [&>div]:flex-col [&>div>span]:text-sm [&>div>span]:text-white/70 [&>button]:rounded-xl [&>button]:bg-white [&>button]:px-4 [&>button]:py-2 [&>button]:text-sm [&>button]:font-bold [&>button]:text-primaryText",
  roleFieldset:
    "md:col-span-3 xl:col-span-6 rounded-2xl border border-borderPrimary bg-bgSecondary p-3",
  roleLegend: "px-1 text-sm font-bold text-primaryText",
  roleOptions: "mt-1 grid grid-cols-1 sm:grid-cols-2 gap-2",
  roleOption:
    "cursor-pointer rounded-xl border border-transparent bg-white px-4 py-3 flex flex-col gap-1 text-sm text-primaryText",
  activeRoleOption: "border-primaryText ring-1 ring-primaryText",
  formError:
    "rounded-xl border border-primary bg-red-50 px-3 py-2 text-sm font-semibold text-primary",
  linkPanel:
    "rounded-2xl border border-borderPrimary bg-white p-4 flex flex-col gap-3",
  memberForm: "grid grid-cols-1 md:grid-cols-3 xl:grid-cols-6 gap-3",
  memberList: "grid grid-cols-1 xl:grid-cols-2 gap-3",
  memberCard:
    "rounded-xl border border-borderPrimary p-3 flex flex-col lg:flex-row lg:items-center justify-between gap-3",
  input:
    "h-11 rounded-xl border border-borderPrimary px-3 outline-none focus:border-primary",
  checkbox: "h-11 flex items-center gap-2 text-sm",
  primaryButton: "h-11 rounded-xl bg-primary text-white font-semibold px-4",
  disabledButton: "opacity-50 cursor-not-allowed",
  list: "flex flex-col gap-3",
  card:
    "rounded-2xl border border-borderPrimary p-4 flex flex-col xl:flex-row xl:items-center justify-between gap-3",
  submissionCard:
    "rounded-2xl border border-borderPrimary p-4 grid grid-cols-1 xl:grid-cols-[280px_1fr] gap-4",
  submissionPhotos: "grid grid-cols-4 xl:grid-cols-2 gap-2",
  submissionThumb:
    "aspect-square overflow-hidden rounded-xl bg-bgSecondary text-xs text-secondaryText flex items-center justify-center capitalize relative",
  fitImage: "h-full w-full object-cover",
  photoBadge:
    "absolute bottom-1 left-1 rounded-full bg-black/70 px-2 py-0.5 text-[10px] font-bold text-white",
  submissionBody: "flex flex-col gap-3 min-w-0",
  rowBetween: "flex flex-col lg:flex-row lg:items-start justify-between gap-3",
  cardTitle: "font-semibold text-primaryText",
  dealerHeading: "flex flex-wrap items-center gap-2",
  accountTypeBadge:
    "rounded-full bg-bgSecondary px-2.5 py-1 text-[11px] font-semibold text-secondaryText",
  activeStatusBadge:
    "rounded-full bg-green-50 px-2.5 py-1 text-[11px] font-semibold text-green-700",
  inactiveStatusBadge:
    "rounded-full bg-red-50 px-2.5 py-1 text-[11px] font-semibold text-red-700",
  trustedBadge:
    "rounded-full bg-amber-50 px-2.5 py-1 text-[11px] font-semibold text-amber-700",
  dealerActions: "flex flex-wrap items-center gap-2",
  meta: "text-sm text-secondaryText mt-1",
  phoneLink: "text-primary font-semibold underline-offset-2 hover:underline",
  warning: "text-sm text-primary mt-1",
  actions: "flex flex-wrap gap-2 text-sm font-semibold text-primary",
  primaryTextButton:
    "rounded-xl bg-primary px-4 py-2 text-sm font-bold text-white",
  successPill:
    "rounded-full bg-green-100 px-3 py-2 text-xs font-bold text-green-700",
  select: "rounded-xl border border-borderPrimary px-3 py-2",
  noteInput: "rounded-xl border border-borderPrimary px-3 py-2",
  publishForm: "grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3",
  publishTextarea: "md:col-span-2 xl:col-span-4 min-h-[88px]",
};

export default DealerMarketplacePage;
